import { useCallback, useEffect, useMemo, useState } from "react";
import { tasksAPI } from "../../api/client";
import {
  DEFAULT_SECTIONS,
  groupTasksBySection,
  sectionTarget,
} from "./sectionUtils";
import { apiError } from "./taskUtils";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";

// Tasks + the viewer's sections, with the actions the sectioned list and the
// board share. `listParams` picks which tasks load (e.g. My Tasks or one
// project's); `createDefaults` is merged into every task created here.
export default function useSectionedTasks(listParams, createDefaults = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [sectionsAreDefault, setSectionsAreDefault] = useState(true);

  const paramsKey = JSON.stringify(listParams);
  const createKey = JSON.stringify(createDefaults);

  const loadTasks = useCallback(async () => {
    try {
      const resp = await tasksAPI.list({
        ...JSON.parse(paramsKey),
        status: "all",
      });
      setTasks(resp.data || []);
      setError("");
    } catch (err) {
      setError(apiError(err, "Could not load tasks"));
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    tasksAPI
      .getSections()
      .then((r) => {
        setSections(r.data.sections);
        setSectionsAreDefault(r.data.isDefault);
      })
      .catch(() => {});
  }, []);

  const groups = useMemo(
    () => groupTasksBySection(tasks, sections),
    [tasks, sections],
  );
  const targetFor = useCallback(
    (section) => sectionTarget(section, sections),
    [sections],
  );
  const canCreate = usePermission(PERMISSIONS.TASK_CREATE);
  const canAddTo = useCallback(
    (section) =>
      canCreate && section.type !== "completed" && Boolean(targetFor(section)),
    [canCreate, targetFor],
  );

  const createIn = async (section, name) => {
    const target = targetFor(section);
    try {
      await tasksAPI.create({
        name,
        dueDate: target?.dueDate ?? null,
        ...JSON.parse(createKey),
      });
      await loadTasks();
      return true;
    } catch (err) {
      setError(apiError(err, "Could not create task"));
      return false;
    }
  };

  const updateOptimistically = async (task, patch, failMessage) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, ...patch } : t)),
    );
    try {
      await tasksAPI.update(task.id, patch);
    } catch (err) {
      setError(apiError(err, failMessage));
    }
    loadTasks();
  };

  const moveTask = (task, section) => {
    const target = targetFor(section);
    if (!target) return;
    if (task.type === "approval" && target.completed) {
      setError(
        "Approvals are closed by approving or rejecting them — open the task to decide.",
      );
      return;
    }
    const patch = target.completed
      ? { completed: true }
      : { completed: false, dueDate: target.dueDate };
    updateOptimistically(task, patch, "Could not move task");
  };

  const toggleComplete = (task) =>
    updateOptimistically(
      task,
      { completed: !task.completed },
      "Could not update task",
    );

  const saveSections = async (next) => {
    const resp = await tasksAPI.saveSections(next);
    setSections(resp.data.sections);
    setSectionsAreDefault(resp.data.isDefault);
  };

  return {
    tasks,
    loading,
    error,
    loadTasks,
    sections,
    sectionsAreDefault,
    saveSections,
    groups,
    targetFor,
    canAddTo,
    createIn,
    moveTask,
    toggleComplete,
  };
}

const LAYOUT_KEY = "df.tasks.layout";

export function readLayout() {
  try {
    return localStorage.getItem(LAYOUT_KEY) === "board" ? "board" : "list";
  } catch {
    return "list";
  }
}

export function storeLayout(layout) {
  try {
    localStorage.setItem(LAYOUT_KEY, layout);
  } catch {
    // storage unavailable — the choice lasts for this visit only
  }
}
